import { tx as translateInterfaceText } from '@/utils/i18n';
import { api } from './api';
import { VideoFeedbackConfig } from '@/store/videoFeatures/types/video-features';
import { VideoFeedback, defaultVideoFeedback } from '@/store/videoFeatures';

export type { VideoFeedbackConfig };

export interface VideoFeedbackConfigResponse {
  haveVideoFeedback: boolean;
  videoFeedbackConfig: VideoFeedbackConfig | null;
}

// Converte o formato do editor (VideoFeedback) para o formato persistido no
// backend e lido pelo player (VideoFeedbackConfig). Mantém compatibilidade
// com o player existente sem alterar seu código.
export function toPlayerConfig(fb: VideoFeedback): VideoFeedbackConfig {
  const ratingTypeMap: Record<VideoFeedback['ratingType'], VideoFeedbackConfig['ratingType']> = {
    emoji: 'emoji',
    stars: 'stars',
    nps: 'number',
  };
  const styleMap: Record<VideoFeedback['appearance']['style'], VideoFeedbackConfig['appearance']['cardStyle']> = {
    'glass-dark': 'dark_glass',
    light: 'light_premium',
    minimal: 'minimalist',
  };
  const positionMap: Record<VideoFeedback['appearance']['position'], VideoFeedbackConfig['appearance']['position']> = {
    'center': 'center',
    'bottom-center': 'bottom_center',
    'bottom-right': 'bottom_right',
  };
  const sizeMap: Record<VideoFeedback['appearance']['size'], VideoFeedbackConfig['appearance']['size']> = {
    compact: 'compact',
    default: 'standard',
    large: 'large',
  };

  const afterSubmitMap: Record<VideoFeedback['afterSubmit']['behavior'], VideoFeedbackConfig['afterSubmit']> = {
    'auto-close': 'auto_close',
    'thanks': 'show_thank_you',
    'keep-open': 'keep_open',
  };

  return {
    ratingType: ratingTypeMap[fb.ratingType],
    triggerType: fb.trigger,
    customTimeSeconds: fb.customTimeSec,
    texts: {
      title: fb.title,
      subtitle: fb.subtitle,
      buttonText: fb.buttonText,
      thankYouMessage: fb.thanksMessage,
    },
    allowComment: fb.comment.enabled,
    commentRequired: fb.comment.required,
    commentPlaceholder: fb.comment.placeholder,
    identificationType: fb.identification,
    appearance: {
      cardStyle: styleMap[fb.appearance.style],
      primaryColor: fb.appearance.color,
      position: positionMap[fb.appearance.position],
      size: sizeMap[fb.appearance.size],
    },
    afterSubmit: afterSubmitMap[fb.afterSubmit.behavior],
    autoCloseDelayMs: fb.afterSubmit.autoCloseSec * 1000,
    allowRepeatAfter24h: fb.allowRepeatAfter24h,
  };
}

// Converte o formato persistido (VideoFeedbackConfig) de volta para o formato
// do editor (VideoFeedback). Usado ao carregar as features de um vídeo.
export function fromPlayerConfig(
  enabled: boolean,
  cfg: VideoFeedbackConfig | null | undefined
): VideoFeedback {
  if (!cfg) {
    return { ...defaultVideoFeedback, enabled };
  }

  const ratingTypeMap: Record<VideoFeedbackConfig['ratingType'], VideoFeedback['ratingType']> = {
    emoji: 'emoji',
    stars: 'stars',
    number: 'nps',
  };
  const styleMap: Record<VideoFeedbackConfig['appearance']['cardStyle'], VideoFeedback['appearance']['style']> = {
    dark_glass: 'glass-dark',
    light_premium: 'light',
    minimalist: 'minimal',
  };
  const positionMap: Record<VideoFeedbackConfig['appearance']['position'], VideoFeedback['appearance']['position']> = {
    center: 'center',
    bottom_center: 'bottom-center',
    bottom_right: 'bottom-right',
  };
  const sizeMap: Record<VideoFeedbackConfig['appearance']['size'], VideoFeedback['appearance']['size']> = {
    compact: 'compact',
    standard: 'default',
    large: 'large',
  };

  const afterSubmitMap: Record<VideoFeedbackConfig['afterSubmit'], VideoFeedback['afterSubmit']['behavior']> = {
    auto_close: 'auto-close',
    show_thank_you: 'thanks',
    keep_open: 'keep-open',
  };

  const rawSec = cfg.autoCloseDelayMs ? Math.round(cfg.autoCloseDelayMs / 1000) : 3;
  const autoCloseSec = ([2, 3, 5, 0].includes(rawSec) ? rawSec : 3) as 2 | 3 | 5 | 0;

  return {
    enabled,
    ratingType: ratingTypeMap[cfg.ratingType] || 'emoji',
    trigger: cfg.triggerType || 'video_end',
    customTimeSec: cfg.customTimeSeconds ?? 0,
    title: cfg.texts?.title ?? defaultVideoFeedback.title,
    subtitle: cfg.texts?.subtitle ?? defaultVideoFeedback.subtitle,
    buttonText: cfg.texts?.buttonText ?? defaultVideoFeedback.buttonText,
    thanksMessage: cfg.texts?.thankYouMessage ?? defaultVideoFeedback.thanksMessage,
    comment: {
      enabled: !!cfg.allowComment,
      required: !!cfg.commentRequired,
      placeholder: cfg.commentPlaceholder ?? defaultVideoFeedback.comment.placeholder,
    },
    identification: cfg.identificationType || 'none',
    appearance: {
      style: styleMap[cfg.appearance?.cardStyle] || 'glass-dark',
      color: cfg.appearance?.primaryColor || defaultVideoFeedback.appearance.color,
      position: positionMap[cfg.appearance?.position] || 'center',
      size: sizeMap[cfg.appearance?.size] || 'default',
    },
    afterSubmit: {
      behavior: afterSubmitMap[cfg.afterSubmit] || 'thanks',
      autoCloseSec,
    },
    allowRepeatAfter24h: !!cfg.allowRepeatAfter24h,
  };
}

export interface FeedbackMetrics {
  video_id: string;
  average_rating: number;
  total_responses: number;
  response_rate: number;
  shown_sessions?: number;
  positive_satisfaction: number;
  distribution: {
    [key: string]: {
      count: number;
      label: string;
    };
  };
  recent_comments: Array<{
    id_feedback: string;
    rating_value: number;
    rating_label: string;
    comment: string;
    viewer_name: string;
    createdAt: string;
    device_type: string;
  }>;
  evolution: Array<{
    date: string;
    average_rating: number;
    responses: number;
  }>;
}

export interface FeedbackResponse {
  id_feedback: string;
  rating_type: string;
  rating_value: number;
  rating_label: string;
  comment?: string;
  viewer_name?: string;
  viewer_email?: string;
  viewer_whatsapp?: string;
  watched_percent?: number;
  watched_seconds?: number;
  trigger_type?: string;
  trigger_time?: number;
  device_type: string;
  browser?: string;
  country?: string;
  city?: string;
  referrer_url?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  session_id?: string;
  createdAt: string;
}

export interface PaginatedResponses {
  responses: FeedbackResponse[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export const videoFeedbackService = {
  getConfig: async (videoId: string): Promise<VideoFeedbackConfigResponse> => {
    const response = await api.get<{ success: boolean; data: VideoFeedbackConfigResponse }>(
      `/video-feedback/config/${videoId}`
    );
    return response.data;
  },

  getMetrics: async (
    videoId: string,
    inicio: string,
    fim: string
  ): Promise<FeedbackMetrics> => {
    const response = await api.get<{ success: boolean; data: FeedbackMetrics }>(
      `/video-feedback/metrics/${videoId}`,
      {
        params: { inicio, fim },
      }
    );
    return response.data;
  },

  listResponses: async (
    videoId: string,
    page: number = 1,
    limit: number = 20,
    filter?: string,
    search?: string
  ): Promise<PaginatedResponses> => {
    const response = await api.get<{ success: boolean; data: PaginatedResponses }>(
      `/video-feedback/responses/${videoId}`,
      {
        params: { page, limit, filter, search },
      }
    );
    return response.data;
  },

  exportData: async (
    videoId: string,
    formato: 'csv' | 'excel' = 'csv',
    inicio?: string,
    fim?: string
  ): Promise<Blob> => {
    const params = new URLSearchParams();
    params.append('formato', formato);
    if (inicio) params.append('inicio', inicio);
    if (fim) params.append('fim', fim);

    const response = await fetch(
      `${import.meta.env.VITE_API_URL || ''}/api/video-feedback/export/${videoId}?${params}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(translateInterfaceText("Erro ao exportar dados"));
    }

    return await response.blob();
  },
};
